// src/utils/audioTones.js
/**
 * Generate simple audio tones using Web Audio API
 * No external files needed!
 */

let audioContext = null
let audioUnlocked = false

export function getAudioContext() {
  if (!audioContext) {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext
    if (!AudioContextClass) {
      console.warn('[AudioTones] Web Audio API not supported')
      return null
    }
    audioContext = new AudioContextClass()
    console.log('[AudioTones] Created audio context, state:', audioContext.state)
  }
  return audioContext
}

// Must be called from a user gesture (click) to unlock audio
export async function unlockAudio() {
  if (audioUnlocked) {
    console.log('[AudioTones] Already unlocked')
    return true
  }

  const ctx = getAudioContext()
  if (!ctx) return false

  console.log('[AudioTones] Attempting to unlock, current state:', ctx.state)

  try {
    if (ctx.state === 'suspended') {
      await ctx.resume()
      console.log('[AudioTones] Resumed context, new state:', ctx.state)
    }

    audioUnlocked = true
    console.log('[AudioTones] Audio unlocked successfully')
    return true
  } catch (e) {
    console.warn('[AudioTones] Could not unlock audio:', e)
    return false
  }
}

/**
 * Silence a looping tone's oscillators NOW, with a 20 ms fade so the cut does not click. A loop's stop must end the ring
 * that is sounding, not only the next one: the ringback sounds 2 s at a time, and a Walkie call answered mid-ring played
 * the connect tone and the answering voice over the rest of that ring
 * (docs/bugs/walkie_ringback_overlaps_answer_BUG.md).
 */
function silenceNow(ctx, sounding) {
  const now = ctx.currentTime
  for (const { osc, gain } of sounding) {
    try {
      gain.gain.cancelScheduledValues(now)
      gain.gain.setValueAtTime(Math.max(gain.gain.value, 0.0001), now)
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.02)
      osc.stop(now + 0.025)
    } catch {
      // Already stopped: nothing is sounding.
    }
  }
}

export function playRingtone() {
  const ctx = getAudioContext()
  if (!ctx) return () => {}

  let isPlaying = true
  let timer = null
  let sounding = []

  function playTone() {
    if (!isPlaying) return

    const osc = ctx.createOscillator()
    const gain = ctx.createGain()

    osc.connect(gain)
    gain.connect(ctx.destination)

    osc.frequency.setValueAtTime(440, ctx.currentTime)
    osc.frequency.setValueAtTime(480, ctx.currentTime + 0.15)
    osc.type = 'sine'

    gain.gain.setValueAtTime(0.3, ctx.currentTime)
    gain.gain.setValueAtTime(0.3, ctx.currentTime + 0.25)
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.4)

    osc.start(ctx.currentTime)
    osc.stop(ctx.currentTime + 0.4)
    sounding = [{ osc, gain }]

    timer = setTimeout(() => {
      if (isPlaying) playTone()
    }, 2000)
  }

  // Mirror playChatNotificationTone: await resume() before scheduling oscillators.
  // Without this, oscillators schedule against a suspended context and never produce
  // audible sound even though the schedule appears to succeed.
  if (ctx.state === 'suspended') {
    console.log('[AudioTones] playRingtone: context suspended, resuming...')
    ctx
      .resume()
      .then(() => {
        console.log('[AudioTones] playRingtone: resumed, state:', ctx.state)
        if (isPlaying) playTone()
      })
      .catch(err => {
        console.warn('[AudioTones] playRingtone: resume failed:', err?.message || err)
      })
  } else {
    playTone()
  }

  // Idempotent: stops the loop, and the chirp sounding now with it.
  return () => {
    if (!isPlaying) return
    isPlaying = false
    clearTimeout(timer)
    silenceNow(ctx, sounding)
    sounding = []
  }
}

// Caller-side ringback for OUTBOUND walkie calls (iPAbVDDV — Evelina: no audible
// cue that the call is actually ringing). Authentic US ringback: 440 Hz + 480 Hz
// played TOGETHER, 2s on / 4s off. Distinct from playRingtone() (the inbound
// chirp) so caller/callee sides are audibly different. Same contract as
// playRingtone: synthesized oscillators (never decoded audio assets — see the
// callStore Twilio BufferSource scar), resume a suspended context before
// scheduling, and return an idempotent stop function.
export function playRingback() {
  const ctx = getAudioContext()
  if (!ctx) return () => {}

  let isPlaying = true
  let timer = null
  let sounding = []

  function playCycle() {
    if (!isPlaying) return

    sounding = []
    for (const freq of [440, 480]) {
      const osc = ctx.createOscillator()
      const gain = ctx.createGain()

      osc.connect(gain)
      gain.connect(ctx.destination)

      osc.frequency.value = freq
      osc.type = 'sine'

      // Two concurrent oscillators — halve the per-oscillator gain vs the
      // single-tone generators so the summed level doesn't clip.
      gain.gain.setValueAtTime(0.15, ctx.currentTime)
      gain.gain.setValueAtTime(0.15, ctx.currentTime + 1.85)
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 2.0)

      osc.start(ctx.currentTime)
      osc.stop(ctx.currentTime + 2.0)
      sounding.push({ osc, gain })
    }

    // 2s on + 4s silence = the standard 6s US ringback cadence.
    timer = setTimeout(() => {
      if (isPlaying) playCycle()
    }, 6000)
  }

  if (ctx.state === 'suspended') {
    console.log('[AudioTones] playRingback: context suspended, resuming...')
    ctx
      .resume()
      .then(() => {
        if (isPlaying) playCycle()
      })
      .catch(err => {
        console.warn('[AudioTones] playRingback: resume failed:', err?.message || err)
      })
  } else {
    playCycle()
  }

  // Idempotent: stops the loop, and the ring sounding now with it, so an answer mid-ring is never heard over the ring.
  return () => {
    if (!isPlaying) return
    isPlaying = false
    clearTimeout(timer)
    silenceNow(ctx, sounding)
    sounding = []
  }
}

export function playConnectTone() {
  const ctx = getAudioContext()
  if (!ctx) return

  if (ctx.state === 'suspended') {
    ctx.resume()
  }

  const osc1 = ctx.createOscillator()
  const gain1 = ctx.createGain()
  osc1.connect(gain1)
  gain1.connect(ctx.destination)
  osc1.frequency.value = 523
  osc1.type = 'sine'
  gain1.gain.setValueAtTime(0.3, ctx.currentTime)
  gain1.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.1)
  osc1.start(ctx.currentTime)
  osc1.stop(ctx.currentTime + 0.1)

  const osc2 = ctx.createOscillator()
  const gain2 = ctx.createGain()
  osc2.connect(gain2)
  gain2.connect(ctx.destination)
  osc2.frequency.value = 659
  osc2.type = 'sine'
  gain2.gain.setValueAtTime(0.3, ctx.currentTime + 0.1)
  gain2.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.25)
  osc2.start(ctx.currentTime + 0.1)
  osc2.stop(ctx.currentTime + 0.25)
}

export function playDisconnectTone() {
  const ctx = getAudioContext()
  if (!ctx) return

  if (ctx.state === 'suspended') {
    ctx.resume()
  }

  const osc = ctx.createOscillator()
  const gain = ctx.createGain()

  osc.connect(gain)
  gain.connect(ctx.destination)

  osc.frequency.setValueAtTime(440, ctx.currentTime)
  osc.frequency.exponentialRampToValueAtTime(220, ctx.currentTime + 0.3)
  osc.type = 'sine'

  gain.gain.setValueAtTime(0.3, ctx.currentTime)
  gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3)

  osc.start(ctx.currentTime)
  osc.stop(ctx.currentTime + 0.3)
}

/**
 * Team chat notification - a pleasant two-note "ding" sound
 * Higher pitched, crisp notification
 */
export function playChatNotificationTone() {
  const ctx = getAudioContext()
  if (!ctx) {
    console.warn('[AudioTones] No audio context')
    return
  }

  console.log('[AudioTones] playChatNotificationTone called, context state:', ctx.state)

  // Try to resume if suspended
  if (ctx.state === 'suspended') {
    console.log('[AudioTones] Context suspended, attempting resume...')
    ctx.resume().then(() => {
      console.log('[AudioTones] Resumed, now playing tone')
      playTheTone(ctx)
    })
  } else {
    playTheTone(ctx)
  }
}

function playTheTone(ctx) {
  console.log('[AudioTones] Playing team chat tone now')

  // A pleasant two-note "ding" sound
  const osc1 = ctx.createOscillator()
  const gain1 = ctx.createGain()
  osc1.connect(gain1)
  gain1.connect(ctx.destination)
  osc1.frequency.value = 830
  osc1.type = 'sine'
  gain1.gain.setValueAtTime(0.2, ctx.currentTime)
  gain1.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.15)
  osc1.start(ctx.currentTime)
  osc1.stop(ctx.currentTime + 0.15)

  const osc2 = ctx.createOscillator()
  const gain2 = ctx.createGain()
  osc2.connect(gain2)
  gain2.connect(ctx.destination)
  osc2.frequency.value = 1046
  osc2.type = 'sine'
  gain2.gain.setValueAtTime(0.2, ctx.currentTime + 0.1)
  gain2.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3)
  osc2.start(ctx.currentTime + 0.1)
  osc2.stop(ctx.currentTime + 0.3)
}

/**
 * Group chat notification - a softer, lower triple-note "bloop" sound
 * Distinctly different from the team chat "ding"
 * Uses a chord arpeggio (G4, C5, E5) for a warmer feel
 */
export function playGroupChatNotificationTone() {
  const ctx = getAudioContext()
  if (!ctx) {
    console.warn('[AudioTones] No audio context')
    return
  }

  console.log('[AudioTones] playGroupChatNotificationTone called, context state:', ctx.state)

  if (ctx.state === 'suspended') {
    console.log('[AudioTones] Context suspended, attempting resume...')
    ctx.resume().then(() => {
      console.log('[AudioTones] Resumed, now playing group tone')
      playGroupTone(ctx)
    })
  } else {
    playGroupTone(ctx)
  }
}

function playGroupTone(ctx) {
  console.log('[AudioTones] Playing group chat tone now')

  // A softer triple-note "bloop" sound - lower frequency than team chat
  // G4 (392Hz), C5 (523Hz), E5 (659Hz) - a pleasant major chord arpeggio
  const notes = [392, 523, 659]
  const startTime = ctx.currentTime

  notes.forEach((freq, i) => {
    const osc = ctx.createOscillator()
    const gain = ctx.createGain()
    osc.connect(gain)
    gain.connect(ctx.destination)
    osc.frequency.value = freq
    osc.type = 'sine'

    const noteStart = startTime + i * 0.08
    gain.gain.setValueAtTime(0.15, noteStart)
    gain.gain.exponentialRampToValueAtTime(0.01, noteStart + 0.12)

    osc.start(noteStart)
    osc.stop(noteStart + 0.12)
  })
}

/**
 * Customer chat notification — a warm two-tone "bell" sound.
 * Distinct from team chat (higher pitch) and group chat (arpeggio).
 * Uses a triangle wave for a softer, warmer quality.
 */
export function playCustomerChatTone() {
  const ctx = getAudioContext()
  if (!ctx) return

  if (ctx.state === 'suspended') {
    ctx.resume().then(() => playCustomerTone(ctx))
  } else {
    playCustomerTone(ctx)
  }
}

function playCustomerTone(ctx) {
  const startTime = ctx.currentTime
  // Two-note bell: A4 (440Hz) then E5 (659Hz) — warm and attention-grabbing
  const notes = [440, 659]

  notes.forEach((freq, i) => {
    const osc = ctx.createOscillator()
    const gain = ctx.createGain()
    osc.connect(gain)
    gain.connect(ctx.destination)
    osc.frequency.value = freq
    osc.type = 'triangle' // Warmer than sine

    const noteStart = startTime + i * 0.15
    gain.gain.setValueAtTime(0.25, noteStart)
    gain.gain.exponentialRampToValueAtTime(0.01, noteStart + 0.3)

    osc.start(noteStart)
    osc.stop(noteStart + 0.3)
  })
}
